/**
 * Wikidata SPARQL backend. Serializes IR into WDQS dialect: `wd:`/`wdt:` prefixes,
 * the `wikibase:label` service for human labels, `COUNT(DISTINCT ...)` for counts.
 * A plain-RDFS/OWL `GenericBackend` (for uploaded ontologies) implements the same
 * interface in M2 and reuses `serializeWhere` below.
 */
import type { Filter, QueryIR, Term, TriplePattern } from "../ir/types.ts";
import { PrefixTable } from "../rdf/prefixes.ts";
import type { Source } from "../sources/types.ts";
import type { CandidateQuery, SparqlBackend } from "./types.ts";

const DEFAULT_SELECT_LIMIT = 100;
const LABEL_LANGUAGES = "en,mul";

export interface WikidataBackendOptions {
  defaultLimit?: number;
  labelLanguages?: string;
}

export class WikidataBackend implements SparqlBackend {
  readonly id = "wikidata";
  private readonly prefixes: PrefixTable;
  private readonly defaultLimit: number;
  private readonly labelLanguages: string;

  constructor(opts: WikidataBackendOptions = {}) {
    this.prefixes = new PrefixTable();
    this.defaultLimit = opts.defaultLimit ?? DEFAULT_SELECT_LIMIT;
    this.labelLanguages = opts.labelLanguages ?? LABEL_LANGUAGES;
  }

  supports(source: Source): boolean {
    return source.backendId === this.id;
  }

  emit(ir: QueryIR, _source?: Source): CandidateQuery[] {
    return [
      {
        sparql: this.serialize(ir),
        strategy: "primary",
        description: describeStrategy(ir),
      },
    ];
  }

  private term(t: Term): string {
    if (t.kind === "var") return "?" + t.name;
    if (t.kind === "iri") {
      const c = this.prefixes.compact(t.value);
      return c === t.value ? `<${t.value}>` : c;
    }
    const quoted = JSON.stringify(t.value);
    if (t.language) return `${quoted}@${t.language}`;
    if (t.datatype) {
      const dt = this.prefixes.compact(t.datatype);
      return `${quoted}^^${dt === t.datatype ? `<${t.datatype}>` : dt}`;
    }
    return quoted;
  }

  private patternLine(p: TriplePattern): string {
    const line = `${this.term(p.subject)} ${this.term(p.predicate)} ${this.term(p.object)} .`;
    return p.optional ? `OPTIONAL { ${line} }` : line;
  }

  private filterLine(f: Filter): string {
    return `FILTER(?${f.variable} ${f.op} ${this.term(f.value)})`;
  }

  private serializeWhere(ir: QueryIR, withLabels: boolean): string {
    const lines = ir.patterns.map((p) => "  " + this.patternLine(p));
    for (const f of ir.filters) lines.push("  " + this.filterLine(f));
    if (withLabels) {
      lines.push(
        `  SERVICE wikibase:label { bd:serviceParam wikibase:language "${this.labelLanguages}". }`,
      );
    }
    return lines.join("\n");
  }

  private serialize(ir: QueryIR): string {
    const header = this.prefixes.header([
      "wd",
      "wdt",
      "p",
      "ps",
      "pq",
      "rdfs",
      "xsd",
      "wikibase",
      "bd",
    ]);

    if (ir.form === "ask") {
      return `${header}\nASK {\n${this.serializeWhere(ir, false)}\n}`;
    }

    if (ir.form === "count") {
      const countVar = ir.projection[0] ?? ir.focusVar;
      const inner = ir.distinct ? `DISTINCT ?${countVar}` : `?${countVar}`;
      return `${header}\nSELECT (COUNT(${inner}) AS ?count) WHERE {\n${this.serializeWhere(ir, false)}\n}`;
    }

    // SELECT
    const distinct = ir.distinct ? "DISTINCT " : "";
    const limit = ir.limit ?? this.defaultLimit;
    const orderClause = ir.orderBy
      ? `ORDER BY ${ir.orderBy.descending ? "DESC" : "ASC"}(?${ir.orderBy.variable})`
      : "";

    if (ir.wantLabels !== true) {
      const vars = ir.projection.map((v) => `?${v}`).join(" ");
      let query = `${header}\nSELECT ${distinct}${vars} WHERE {\n${this.serializeWhere(ir, false)}\n}`;
      if (orderClause) query += `\n${orderClause}`;
      return query + `\nLIMIT ${limit}`;
    }

    // Labeled SELECT: do the matching/ordering/limiting in an inner subquery so
    // the (expensive) label service only ever resolves the final rows — not the
    // entire class first. This is the difference between 0.2s and 80s+ on WDQS.
    const innerVars = ir.projection.map((v) => `?${v}`).join(" ");
    let inner = `SELECT ${distinct}${innerVars} WHERE {\n${this.serializeWhere(ir, false)}\n  }`;
    if (orderClause) inner += `\n  ${orderClause}`;
    inner += `\n  LIMIT ${limit}`;

    const outerVars: string[] = [];
    for (const varName of ir.projection) {
      outerVars.push(`?${varName}`);
      if (varName !== ir.orderBy?.variable) outerVars.push(`?${varName}Label`);
    }
    let query =
      `${header}\nSELECT ${outerVars.join(" ")} WHERE {\n  {\n  ${inner}\n  }\n` +
      `  SERVICE wikibase:label { bd:serviceParam wikibase:language "${this.labelLanguages}". }\n}`;
    if (orderClause) query += `\n${orderClause}`;
    return query;
  }
}

function describeStrategy(ir: QueryIR): string {
  switch (ir.form) {
    case "count":
      return "Counts distinct matches with COUNT(DISTINCT …).";
    case "ask":
      return "Yes/no existence check with ASK.";
    default:
      return ir.orderBy
        ? `Ranks by ?${ir.orderBy.variable} (${ir.orderBy.descending ? "desc" : "asc"}) and takes the top result.`
        : "Selects matching entities, resolving human labels via the Wikidata label service.";
  }
}
